import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Newspaper, Plus, Info, AlertTriangle, Sparkles, X, Pin, Pencil, Trash2, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { useAuth, hasPerm } from "@/context/AuthContext";
import { useResourceList, useResourceMutations } from "@/hooks/useResource";
import { listResource } from "@/lib/api";
import { PageHeader } from "@/components/kit/PageHeader";
import { Button } from "@/components/ui/button";
import { FormDrawer } from "@/components/kit/FormDrawer";
import { TextField, TextareaField, SelectField, SwitchField } from "@/components/kit/fields";
import { FileUpload, resolveAsset } from "@/components/kit/FileUpload";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/format";

const TYPES = {
  info: { label: "Info", icon: Info, color: "#3B82F6" },
  warning: { label: "Warning", icon: AlertTriangle, color: "#EAB308" },
  update: { label: "Update", icon: Sparkles, color: "#22C55E" },
};
const AUDIENCES = [
  { value: "everyone", label: "Everyone" },
  { value: "managers", label: "Managers only" },
  { value: "buyers", label: "Buyers only" },
  { value: "advertisers", label: "Advertisers only" },
  { value: "selected", label: "Selected users" },
];
const STATUS_BADGE = {
  draft: "bg-white/[0.08] text-muted-foreground",
  scheduled: "bg-[#38BDF8]/15 text-[#38BDF8]",
  expired: "bg-[#EF4444]/15 text-[#EF4444]",
};
const EMPTY_FORM = { title: "", body: "", type: "info", cover_image: "", audience: "everyone",
  audience_user_ids: [], publish_mode: "now", publish_at: "", expire_mode: "never", expires_at: "", pinned: false };

const toLocal = (iso) => (iso ? String(iso).slice(0, 16) : "");

function TypeTag({ type }) {
  const t = TYPES[type] || TYPES.info;
  const Icon = t.icon;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium"
      style={{ backgroundColor: `${t.color}1a`, color: t.color }}>
      <Icon className="h-3 w-3" /> {t.label}
    </span>
  );
}

function StatusTag({ status, testId }) {
  if (!status || status === "published") return null;
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_BADGE[status] || ""}`} data-testid={testId}>{status}</span>;
}

function DateTimeField({ label, value, onChange, testId }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      <input type="datetime-local" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="w-full rounded-lg border border-white/[0.1] bg-[#0A0A0A] px-3 py-2 text-sm text-foreground [color-scheme:dark]" />
    </div>
  );
}

function UserPicker({ users, value, onChange }) {
  return (
    <div className="max-h-44 space-y-0.5 overflow-y-auto rounded-lg border border-white/[0.1] bg-[#0A0A0A] p-1.5" data-testid="news-user-picker">
      {users.length === 0 && <p className="px-2 py-2 text-xs text-muted-foreground">No users found.</p>}
      {users.map((u) => {
        const on = value.includes(u.id);
        return (
          <label key={u.id} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground/90 hover:bg-white/[0.04]" data-testid={`news-user-${u.id}`}>
            <input type="checkbox" checked={on} onChange={() => onChange(on ? value.filter((x) => x !== u.id) : [...value, u.id])} className="accent-white" />
            <span className="truncate">{u.name || u.email}</span>
            <span className="ml-auto shrink-0 text-[11px] capitalize text-muted-foreground">{(u.role || "").replace("_", " ")}</span>
          </label>
        );
      })}
    </div>
  );
}

export default function News() {
  const { user } = useAuth();
  const canManage = hasPerm(user, "announcements.create");
  const { data, isLoading } = useResourceList("announcements", {});
  const { data: usersData } = useQuery({ queryKey: ["users", "news-picker"], queryFn: () => listResource("users", {}), enabled: canManage });
  const { create, update, remove } = useResourceMutations("announcements");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [active, setActive] = useState(null);
  const [params, setParams] = useSearchParams();
  const [form, setForm] = useState(EMPTY_FORM);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const items = React.useMemo(() => data?.items || [], [data]);
  const users = usersData?.items || [];

  // Deep-link from a notification: /news?open={id}
  useEffect(() => {
    const id = params.get("open");
    if (id && items.length) {
      const found = items.find((x) => x.id === id);
      if (found) setActive(found);
    }
  }, [params, items]);

  const closeModal = () => { setActive(null); if (params.get("open")) { params.delete("open"); setParams(params, { replace: true }); } };

  const openCreate = () => { setEditId(null); setForm(EMPTY_FORM); setOpen(true); };
  const openEdit = (a) => {
    setEditId(a.id);
    setForm({
      title: a.title || "", body: a.body || "", type: a.type || "info", cover_image: a.cover_image || "",
      audience: a.audience || "everyone", audience_user_ids: a.audience_user_ids || [],
      publish_mode: a.status === "draft" ? "draft" : (a.publish_at ? "schedule" : "now"),
      publish_at: toLocal(a.publish_at),
      expire_mode: a.expires_at ? "at" : "never", expires_at: toLocal(a.expires_at),
      pinned: !!a.pinned,
    });
    closeModal();
    setOpen(true);
  };

  const save = async () => {
    if (!form.title) return toast.error("Title is required");
    if (form.publish_mode === "schedule" && !form.publish_at) return toast.error("Pick the publish date & time");
    if (form.expire_mode === "at" && !form.expires_at) return toast.error("Pick the expiry date & time");
    if (form.audience === "selected" && !form.audience_user_ids.length) return toast.error("Select at least one user");
    const body = {
      title: form.title, body: form.body, type: form.type, cover_image: form.cover_image,
      audience: form.audience,
      audience_user_ids: form.audience === "selected" ? form.audience_user_ids : [],
      status: form.publish_mode === "draft" ? "draft" : "published",
      publish_at: form.publish_mode === "schedule" ? form.publish_at : null,
      expires_at: form.expire_mode === "at" ? form.expires_at : null,
      pinned: form.pinned,
    };
    if (editId) {
      await update.mutateAsync({ id: editId, body });
      toast.success("News updated");
    } else {
      await create.mutateAsync(body);
      toast.success(form.publish_mode === "draft" ? "Draft saved" :
        form.publish_mode === "schedule" ? "News scheduled" : "News published — the selected audience was notified");
    }
    setForm(EMPTY_FORM); setEditId(null); setOpen(false);
  };

  const del = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    await remove.mutateAsync(a.id);
    toast.success("News deleted");
    closeModal();
  };

  return (
    <div className="space-y-5">
      <PageHeader title="News" subtitle="Updates and announcements for your partner network.">
        {canManage && (
          <Button onClick={openCreate} data-testid="add-news-btn" className="h-9 gap-1.5 bg-white text-black hover:bg-white/90">
            <Plus className="h-4 w-4" /> New post
          </Button>
        )}
      </PageHeader>

      {isLoading && <p className="text-sm text-muted-foreground">Loading...</p>}
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
        {items.map((a) => {
          const cover = resolveAsset(a.cover_image);
          const t = TYPES[a.type] || TYPES.info;
          return (
            <button key={a.id} onClick={() => setActive(a)} data-testid={`news-card-${a.id}`}
              className="group overflow-hidden rounded-xl border border-white/[0.08] bg-[#141417] text-left transition-all hover:border-white/20 hover:shadow-lg">
              <div className="relative h-40 w-full overflow-hidden">
                {cover
                  ? <img src={cover} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  : <div className="flex h-full w-full items-center justify-center" style={{ background: `linear-gradient(135deg, ${t.color}22, #0A0A0A)` }}><t.icon className="h-8 w-8" style={{ color: t.color }} strokeWidth={1.25} /></div>}
                <div className="absolute left-3 top-3 flex items-center gap-2">
                  <TypeTag type={a.type} />
                  <StatusTag status={a.derived_status} testId={`news-status-${a.id}`} />
                </div>
                {a.pinned && (
                  <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-[#EAB308]/20 px-2 py-0.5 text-xs font-medium text-[#EAB308] backdrop-blur-sm" data-testid={`news-pinned-${a.id}`}>
                    <Pin className="h-3 w-3" /> Pinned
                  </span>
                )}
              </div>
              <div className="p-4">
                <h3 className="line-clamp-2 text-sm font-semibold text-foreground">{a.title}</h3>
                <p className="mt-1.5 line-clamp-2 text-sm text-muted-foreground">{a.body}</p>
                <p className="mt-3 text-xs text-muted-foreground/60">{fmtDate(a.created_at)}</p>
              </div>
            </button>
          );
        })}
        {!isLoading && items.length === 0 && (
          <div className="col-span-full flex flex-col items-center gap-2 py-16 text-muted-foreground">
            <Newspaper className="h-8 w-8" strokeWidth={1.25} /><span className="text-sm">No news yet.</span>
          </div>
        )}
      </div>

      {/* Detail modal */}
      <Dialog open={!!active} onOpenChange={(o) => !o && closeModal()}>
        <DialogContent className="max-w-2xl overflow-hidden border-white/[0.08] bg-[#141417] p-0" data-testid="news-detail-modal">
          {active && (
            <div className="max-h-[85vh] overflow-y-auto">
              <div className="relative h-56 w-full overflow-hidden">
                {resolveAsset(active.cover_image)
                  ? <img src={resolveAsset(active.cover_image)} alt="" className="h-full w-full object-cover" />
                  : <div className="flex h-full w-full items-center justify-center" style={{ background: `linear-gradient(135deg, ${(TYPES[active.type] || TYPES.info).color}33, #0A0A0A)` }}>
                      {React.createElement((TYPES[active.type] || TYPES.info).icon, { className: "h-12 w-12", style: { color: (TYPES[active.type] || TYPES.info).color }, strokeWidth: 1 })}
                    </div>}
                <button onClick={closeModal} className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70" data-testid="news-close"><X className="h-4 w-4" /></button>
              </div>
              <div className="p-6">
                <div className="mb-3 flex flex-wrap items-center gap-3">
                  <TypeTag type={active.type} />
                  <StatusTag status={active.derived_status} />
                  {active.pinned && <span className="inline-flex items-center gap-1 rounded-full bg-[#EAB308]/15 px-2 py-0.5 text-xs font-medium text-[#EAB308]"><Pin className="h-3 w-3" /> Pinned</span>}
                  <span className="text-xs text-muted-foreground">{fmtDate(active.created_at)}</span>
                </div>
                <h2 className="text-2xl font-semibold text-foreground">{active.title}</h2>
                <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-foreground/80">{active.body}</p>
                {canManage && (
                  <div className="mt-5 space-y-1 border-t border-white/[0.06] pt-3 text-xs text-muted-foreground" data-testid="news-manage-meta">
                    <div>Audience: <span className="text-foreground/80">{(AUDIENCES.find((x) => x.value === (active.audience || "everyone")) || {}).label}</span>
                      {active.audience === "selected" && <span> — {(active.audience_user_ids || []).map((id) => (users.find((u) => u.id === id) || {}).name || (users.find((u) => u.id === id) || {}).email || id).join(", ")}</span>}
                    </div>
                    {active.publish_at && <div className="flex items-center gap-1"><CalendarClock className="h-3 w-3" /> Publish at: {String(active.publish_at).replace("T", " ")} UTC</div>}
                    <div>Expires: <span className="text-foreground/80">{active.expires_at ? `${String(active.expires_at).replace("T", " ")} UTC` : "never"}</span></div>
                  </div>
                )}
                {canManage && (
                  <div className="mt-4 flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => openEdit(active)} data-testid="news-edit-btn" className="gap-1.5 border-white/[0.12]"><Pencil className="h-3.5 w-3.5" /> Edit</Button>
                    <Button variant="outline" size="sm" onClick={() => del(active)} data-testid="news-delete-btn" className="gap-1.5 border-white/[0.12] text-[#EF4444]"><Trash2 className="h-3.5 w-3.5" /> Delete</Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <FormDrawer open={open} onOpenChange={setOpen} title={editId ? "Edit post" : "New post"}
        subtitle={editId ? "Update this announcement" : "Publish news to your network"} size="md"
        onSave={save} saving={create.isPending || update.isPending}
        saveLabel={editId ? "Save changes" : (form.publish_mode === "draft" ? "Save draft" : form.publish_mode === "schedule" ? "Schedule" : "Publish")}
        testId="news-drawer">
        <div className="space-y-5">
          <FileUpload label="Cover image" value={form.cover_image} onChange={(v) => set("cover_image", v)} kind="login_bg" testId="news-cover" hint="Optional · max 5MB" />
          <TextField label="Title" value={form.title} onChange={(v) => set("title", v)} testId="news-title" />
          <SelectField label="Type" value={form.type} onChange={(v) => set("type", v)} options={Object.entries(TYPES).map(([value, t]) => ({ value, label: t.label }))} testId="news-type" />
          <TextareaField label="Text" value={form.body} onChange={(v) => set("body", v)} rows={6} testId="news-body" />

          <SelectField label="Audience" value={form.audience} onChange={(v) => set("audience", v)} options={AUDIENCES} testId="news-audience" />
          {form.audience === "selected" && <UserPicker users={users} value={form.audience_user_ids} onChange={(v) => set("audience_user_ids", v)} />}

          <SelectField label="Publishing" value={form.publish_mode} onChange={(v) => set("publish_mode", v)} testId="news-publish-mode"
            options={[{ value: "now", label: "Publish immediately" }, { value: "schedule", label: "Schedule publish" }, { value: "draft", label: "Save as draft" }]} />
          {form.publish_mode === "schedule" && <DateTimeField label="Publish at (UTC)" value={form.publish_at} onChange={(v) => set("publish_at", v)} testId="news-publish-at" />}

          <SelectField label="Expiration" value={form.expire_mode} onChange={(v) => set("expire_mode", v)} testId="news-expire-mode"
            options={[{ value: "never", label: "Never expire" }, { value: "at", label: "Expire at…" }]} />
          {form.expire_mode === "at" && <DateTimeField label="Expire at (UTC)" value={form.expires_at} onChange={(v) => set("expires_at", v)} testId="news-expires-at" />}

          <SwitchField label="Pinned" hint="Pinned news always appear at the top." checked={form.pinned} onChange={(v) => set("pinned", v)} testId="news-pinned" />
        </div>
      </FormDrawer>
    </div>
  );
}
